import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { 
  ArrowRight, 
  CheckCircle, 
  Clock, 
  FileText, 
  BarChart3,
  Zap,
  UserRound,
  Headphones,
  MessagesSquare,
  ScanSearch,
  Brain,
  Award,
  GraduationCap,
  Check,
  Badge,
  X
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { fetchKnitAppsFromJson } from '@/services/knitService';
import FrequentlyAskedQuestions from '@/components/shared/FrequentlyAskedQuestions';
import ProvenResults from '@/components/shared/ProvenResults';
import { format } from 'date-fns';
import * as publicBlogApi from '@/api/publicBlogApi';
import { Blog } from '@/types/blog';
import SEO from '@/components/shared/SEO';

// Define the KnitApp interface to match the one in knitService
interface KnitApp {
  id: string;
  label: string;
  logo_short: string;
  logo_full: string;
  category: string;
}

// Hero image placeholders
const RecruitersImg = '/images/recruiters-graphic.png';
const FeaturesImg = '/images/features-graphic.png';
const PracticeInterviewImg = '/images/practice-interview.png';

const HomePage = () => {
  const [atsApps, setAtsApps] = useState<KnitApp[]>([]);
  const [visibleAtsApps, setVisibleAtsApps] = useState<KnitApp[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedInterval, setSelectedInterval] = useState<'month' | 'year'>('month');
  const [scrollY, setScrollY] = useState(0);
  const [blogPosts, setBlogPosts] = useState<Blog[]>([]);
  const [blogsLoading, setBlogsLoading] = useState(true);
  
  // Custom CSS for animations
  const animationStyles = `
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    .animate-fadeIn {
      animation: fadeIn 0.8s ease-out forwards;
    }
    
    @keyframes fadeOut {
      from { opacity: 1; transform: translateY(0); }
      to { opacity: 0; transform: translateY(-10px); }
    }
    
    .animate-fadeOut {
      animation: fadeOut 0.8s ease-out forwards;
    }
    
    .dashboard-image-container {
      transition: transform 0.8s cubic-bezier(0.25, 0.1, 0.25, 1); 
    }
    
    .dashboard-image {
      transition: all 0.8s cubic-bezier(0.25, 0.1, 0.25, 1);
      will-change: transform, box-shadow;
    }
    
    .dashboard-image img {
      transition: transform 0.8s cubic-bezier(0.25, 0.1, 0.25, 1);
      will-change: transform;
    }
  `;
  
  useEffect(() => {
    // Function to handle scroll with throttling for better performance
    let lastScrollTime = 0;
    const throttleTime = 10; // ms between updates
    
    const handleScroll = () => {
      const now = Date.now();
      if (now - lastScrollTime >= throttleTime) {
        lastScrollTime = now;
        setScrollY(window.scrollY);
      }
    };

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Clean up function
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  useEffect(() => {
    // Fetch ATS apps on component mount
    const loadAtsApps = async () => {
      try {
        const apps = await fetchKnitAppsFromJson();
        setAtsApps(apps);
      } catch (error) {
        console.error('Error loading ATS apps:', error);
      }
    };
    
    loadAtsApps();
  }, []);
  
  // Effect to handle rotating the visible ATS apps
  useEffect(() => {
    if (!atsApps.length) return;
    
    if (atsApps.length <= 4) {
      setVisibleAtsApps(atsApps);
      return;
    }
    
    // Set initial visible apps immediately - first 4 apps
    setVisibleAtsApps(atsApps.slice(0, 4));
    
    // Wait a significant time before starting the rotation
    const firstRotationDelay = setTimeout(() => {
      // Start the rotation after initial delay
      const interval = setInterval(() => {
        setCurrentSlide(prev => {
          const nextSlide = (prev + 1) % Math.ceil(atsApps.length / 4);
          const startIndex = nextSlide * 4;
          const endIndex = Math.min(startIndex + 4, atsApps.length);
          
          // Simple display of the next set of apps without animation
          if (endIndex > atsApps.length) {
            const firstPart = atsApps.slice(startIndex);
            const secondPart = atsApps.slice(0, endIndex - atsApps.length);
            setVisibleAtsApps([...firstPart, ...secondPart]);
          } else {
            setVisibleAtsApps(atsApps.slice(startIndex, endIndex));
          }
          
          return nextSlide;
        });
      }, 8000); // Increased to 8 seconds for slower rotation
      
      return () => clearInterval(interval);
    }, 15000); // Increased to 15 seconds before first transition
    
    return () => clearTimeout(firstRotationDelay);
  }, [atsApps]);
  
  useEffect(() => {
    // Fetch blog posts on component mount
    const loadBlogPosts = async () => {
      try {
        setBlogsLoading(true);
        const response = await publicBlogApi.getPublicBlogs(1, 3);
        setBlogPosts(response.data || []);
      } catch (error) {
        console.error('Error loading blog posts:', error);
        setBlogPosts([]);
      } finally {
        setBlogsLoading(false);
      }
    };
    
    loadBlogPosts();
  }, []);
  
  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMMM dd, yyyy');
  };
  
  // Create structured data for the homepage
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "RoundOne AI",
    "url": "https://roundone.ai",
    "description": "AI-powered candidate assessment platform for smarter hiring decisions",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://roundone.ai/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  // Create organization structured data
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "RoundOne AI",
    "url": "https://roundone.ai",
    "logo": "https://roundone.ai/images/dark-logo.png",
    "sameAs": [
      "https://twitter.com/roundoneai",
      "https://www.linkedin.com/company/roundoneai",
      "https://www.facebook.com/roundoneai"
    ]
  };

  // Create software application structured data
  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "RoundOne AI Assessment Platform",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web-based",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "GBP"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.5",
      "ratingCount": "100"
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="RoundOne AI - Intelligent Candidate Assessment Platform"
        description="Transform your hiring process with AI-powered candidate screening. Save time, reduce costs, and make better hiring decisions with our intelligent assessment platform."
        schema={[structuredData, organizationSchema, softwareSchema]}
      />
      
      {/* Add animation styles */}
      <style dangerouslySetInnerHTML={{ __html: animationStyles }} />
      
      {/* Use the shared Header component */}
      <Header />

      {/* Hero Section */}
      <section className="pt-16 md:pt-24 pb-72 md:pb-96 bg-white relative">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center text-center max-w-3xl mx-auto">
            <div className="mb-6">
              <Link to="/blog/roundone-ai-2-0-a-revolutionary-leap-forward-in-ai-driven-recruitment">
                <span className="inline-block text-xs bg-roundone-light-purple/10 text-roundone-purple px-3 py-1 rounded-full mb-4">
                  <span className="font-bold">NEW</span> <span className="ml-1">RoundOne AI 2.0 is here! →</span>
                </span>
              </Link>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              <span className="text-roundone-dark-purple">Shortlist Smarter:</span>
              <span className="text-roundone-light-purple"> AI-Driven Candidate Screening</span>
            </h1>
            <p className="text-lg md:text-xl mb-8 text-gray-700">
              Say goodbye to CV clutter. Identify top talent with data-backed skill evaluations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md mx-auto mb-6">
              <Link to="/sign-in?tab=register" className="w-full">
                <Button 
                  className="gradient-button py-6 px-8 text-lg w-full rounded-full transition-all duration-300 hover:shadow-lg hover:scale-105 flex items-center justify-center" 
                >
                  Get started for free
                  <Zap className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            <div className="flex items-center justify-center mb-24 md:mb-32">
              <div className="flex items-center text-gray-600">
                <div className="flex">
                  {[1, 2, 3, 4, 4.5].map((rating, index) => (
                    <svg 
                      key={index} 
                      className={`w-5 h-5 ${rating === 4.5 ? 'text-yellow-400 fill-current stroke-current' : 'text-yellow-400'}`} 
                      fill={rating === 4.5 ? 'url(#half-gradient)' : 'currentColor'} 
                      viewBox="0 0 20 20"
                    >
                      {rating === 4.5 ? (
                        <>
                          <defs>
                            <linearGradient id="half-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="50%" stopColor="currentColor" />
                              <stop offset="50%" stopColor="none" stopOpacity="0" />
                            </linearGradient>
                          </defs>
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                        </>
                      ) : (
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                      )}
                    </svg>
                  ))}
                </div>
                <span className="ml-2">100+ Smart Recruiters using RoundOne AI</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Dashboard image that spans between sections */}
        <div className="absolute left-0 right-0 bottom-0 translate-y-1/3 flex justify-center px-4">
          <div 
            className="w-full max-w-6xl dashboard-image-container"
            style={{
              transform: `scale(${Math.min(1 + scrollY * 0.0003, 1.08)})`,
            }}
          >
            <div 
              className="bg-roundone-dark-purple rounded-xl p-4 shadow-2xl overflow-hidden border border-[#E5DEFF] dashboard-image"
              style={{
                transform: `translateY(${Math.min(scrollY * 0.07, 25)}px)`,
                boxShadow: `0 ${Math.min(10 + scrollY * 0.03, 20)}px ${Math.min(15 + scrollY * 0.05, 35)}px rgba(94, 75, 210, ${Math.min(0.2 + scrollY * 0.0005, 0.35)})`,
              }}
            >
              <img 
                src="/images/dashboard-screenshot.png" 
                alt="RoundOne AI Dashboard" 
                className="w-full h-auto rounded-lg"
                style={{
                  transform: `scale(${Math.min(1 + scrollY * 0.0001, 1.03)})`,
                }}
                onError={(e) => {
                  e.currentTarget.src = 'https://placehold.co/1200x600/1A0B36/9b87f5?text=RoundOne+Dashboard';
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Visual separator between Hero and How It Works sections */}
      <div className="w-full bg-gradient-to-r from-transparent via-[#9b87f5]/30 to-transparent h-px"></div>

      {/* Benefits Section with padding to accommodate the image */}
      <section className="pt-72 md:pt-96 pb-16 md:pb-20 bg-[#F8F7FF]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 mt-24">
            <p className="uppercase text-sm font-medium tracking-wider text-[#9b87f5] mb-2">HOW IT WORKS</p>
            <h2 className="text-3xl font-bold mb-4">
              Drowning in Resumes? Stop Wasting Time & Money
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              RoundOne AI transforms your screening process in 4 simple steps
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {/* Step 1 */}
            <div className="relative h-full">
              <div className="bg-white p-6 rounded-xl border border-[#9b87f5]/20 shadow-sm h-full flex flex-col">
                <div className="w-12 h-12 bg-[#9b87f5] rounded-full flex items-center justify-center text-white font-bold mb-4 flex-shrink-0">
                  1
                </div>
                <h3 className="text-lg font-semibold text-[#1A1F2C] mb-2">Create Assessments</h3>
                <p className="text-[#1A1F2C]/70 flex-grow">
                  Select skills and build custom assessments tailored to your roles in minutes, not hours.
                </p>
              </div>
              
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="#9b87f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            
            {/* Step 2 */}
            <div className="relative h-full">
              <div className="bg-white p-6 rounded-xl border border-[#9b87f5]/20 shadow-sm h-full flex flex-col">
                <div className="w-12 h-12 bg-[#9b87f5] rounded-full flex items-center justify-center text-white font-bold mb-4 flex-shrink-0">
                  2
                </div>
                <h3 className="text-lg font-semibold text-[#1A1F2C] mb-2">Screen Candidates</h3>
                <p className="text-[#1A1F2C]/70 flex-grow">
                  Send invites or let candidates complete phone screenings with our AI interviewer - cut admin time by 70%.
                </p>
              </div>
              
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="#9b87f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            
            {/* Step 3 */}
            <div className="relative h-full">
              <div className="bg-white p-6 rounded-xl border border-[#9b87f5]/20 shadow-sm h-full flex flex-col">
                <div className="w-12 h-12 bg-[#9b87f5] rounded-full flex items-center justify-center text-white font-bold mb-4 flex-shrink-0">
                  3
                </div>
                <h3 className="text-lg font-semibold text-[#1A1F2C] mb-2">Get Insights</h3>
                <p className="text-[#1A1F2C]/70 flex-grow">
                  Receive detailed candidate evaluations with skill scores and AI-generated summaries.
                </p>
              </div>
              
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="#9b87f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            
            {/* Step 4 */}
            <div className="h-full">
              <div className="bg-white p-6 rounded-xl border border-[#9b87f5]/20 shadow-sm h-full flex flex-col">
                <div className="w-12 h-12 bg-[#9b87f5] rounded-full flex items-center justify-center text-white font-bold mb-4 flex-shrink-0">
                  4
                </div>
                <h3 className="text-lg font-semibold text-[#1A1F2C] mb-2">Hire Faster</h3>
                <p className="text-[#1A1F2C]/70 flex-grow">
                  Make data-driven decisions and reduce time-to-hire by up to 60% with clear candidate rankings.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Visual separator */}
      <div className="h-px max-w-6xl mx-auto bg-gradient-to-r from-transparent via-[#9b87f5]/30 to-transparent"></div>

      {/* ATS Integrations Section */}
      <section className="py-16 md:py-20 relative overflow-hidden">
        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#E9E5FF] to-[#F0EEFF]"></div>
        
        {/* Content with subtle elevation */}
        <div className="container relative mx-auto px-4 max-w-7xl">
          <div className="text-center mb-12">
            <p className="uppercase text-sm font-medium tracking-wider text-[#9b87f5] mb-2">SEAMLESS INTEGRATIONS</p>
            <h2 className="text-3xl font-bold mb-4">Connect With Your Favorite ATS</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              RoundOne integrates with top Applicant Tracking Systems for a frictionless workflow
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center max-w-6xl mx-auto">
            <div>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-[#F8F7FF] p-2 rounded-full">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#9b87f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                      <path d="M21 12a9 9 0 0 1-9 9m9-9a9 9 0 0 0-9-9m9 9H3m9 9a9 9 0 0 1-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium">Automated Data Sync</h3>
                    <p className="text-muted-foreground text-sm">Candidate data flows automatically between platforms</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-[#F8F7FF] p-2 rounded-full">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#9b87f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                      <path d="M2 20h20" />
                      <path d="M5 20V8.5A3.5 3.5 0 0 1 8.5 5H15" />
                      <path d="M20.83 23 15 15.5A3.5 3.5 0 0 1 19.5 8a3.5 3.5 0 0 1 1 6.5L16 23" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium">Unified Workflow</h3>
                    <p className="text-muted-foreground text-sm">Manage hiring from a single platform, no switching</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-[#F8F7FF] p-2 rounded-full">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#9b87f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                      <path d="M12 6v6l3 3" />
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium">Real-time Updates</h3>
                    <p className="text-muted-foreground text-sm">Screening results sync instantly to your ATS</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8">
                <Link to="/ats-integrations">
                  <Button 
                    className="bg-roundone-dark-purple text-white py-6 px-8 text-lg rounded-full transition-all duration-300 hover:bg-roundone-purple hover:shadow-md flex items-center justify-center"
                  >
                    See All Integrations
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-sm border border-[#E5DEFF]">
                <div className="grid grid-cols-2 gap-4 mb-4 relative overflow-hidden" style={{ minHeight: "160px" }}>
                  {visibleAtsApps.length > 0 ? (
                    visibleAtsApps.map((app, index) => (
                      <div 
                        key={`${app.id}-${currentSlide}-${index}`}
                        className="bg-white border border-[#E5DEFF] rounded-lg p-4 flex flex-col items-center justify-center hover:border-[#9b87f5] hover:shadow-sm transition-all duration-500 animate-fadeIn ats-logo-item"
                      >
                        <img 
                          src={app.logo_short || `/ats-logos/${app.id}.png`} 
                          alt={app.label} 
                          className="w-12 h-12 object-contain mb-2 transition-transform duration-500 hover:scale-110" 
                          onError={(e) => {
                            e.currentTarget.src = `https://placehold.co/100x100/F8F7FF/9b87f5?text=${encodeURIComponent(app.label)}`;
                          }}
                        />
                        <h3 className="text-center text-sm font-medium">{app.label}</h3>
                      </div>
                    ))
                  ) : (
                    // Fallback ATS platforms if cache is empty
                    <>
                      <div className="bg-white border border-[#E5DEFF] rounded-lg p-4 flex flex-col items-center justify-center hover:border-[#9b87f5] hover:shadow-sm transition-all duration-500 animate-fadeIn ats-logo-item">
                        <img src="/ats-logos/greenhouse.png" alt="Greenhouse" className="w-12 h-12 object-contain mb-2 transition-transform duration-500 hover:scale-110" 
                          onError={(e) => {
                            e.currentTarget.src = 'https://placehold.co/100x100/F8F7FF/9b87f5?text=Greenhouse';
                          }}
                        />
                        <h3 className="text-center text-sm font-medium">Greenhouse</h3>
                      </div>
                      <div className="bg-white border border-[#E5DEFF] rounded-lg p-4 flex flex-col items-center justify-center hover:border-[#9b87f5] hover:shadow-sm transition-all duration-500 animate-fadeIn ats-logo-item">
                        <img src="/ats-logos/ashby.png" alt="Ashby" className="w-12 h-12 object-contain mb-2 transition-transform duration-500 hover:scale-110" 
                          onError={(e) => {
                            e.currentTarget.src = 'https://placehold.co/100x100/F8F7FF/9b87f5?text=Ashby';
                          }}
                        />
                        <h3 className="text-center text-sm font-medium">Ashby</h3>
                      </div>
                      <div className="bg-white border border-[#E5DEFF] rounded-lg p-4 flex flex-col items-center justify-center hover:border-[#9b87f5] hover:shadow-sm transition-all duration-500 animate-fadeIn ats-logo-item">
                        <img src="/ats-logos/teamtailor.png" alt="Team Tailor" className="w-12 h-12 object-contain mb-2 transition-transform duration-500 hover:scale-110" 
                          onError={(e) => {
                            e.currentTarget.src = 'https://placehold.co/100x100/F8F7FF/9b87f5?text=Team+Tailor';
                          }}
                        />
                        <h3 className="text-center text-sm font-medium">Team Tailor</h3>
                      </div>
                      <div className="bg-white border border-[#E5DEFF] rounded-lg p-4 flex flex-col items-center justify-center hover:border-[#9b87f5] hover:shadow-sm transition-all duration-500 animate-fadeIn ats-logo-item">
                        <img src="/ats-logos/bullhorn.png" alt="Bullhorn" className="w-12 h-12 object-contain mb-2 transition-transform duration-500 hover:scale-110" 
                          onError={(e) => {
                            e.currentTarget.src = 'https://placehold.co/100x100/F8F7FF/9b87f5?text=Bullhorn';
                          }}
                        />
                        <h3 className="text-center text-sm font-medium">Bullhorn</h3>
                      </div>
                    </>
                  )}
                </div>
                
                {/* Carousel indicators */}
                {atsApps.length > 4 && (
                  <div className="flex justify-center gap-1 mb-2">
                    {Array.from({ length: Math.ceil(atsApps.length / 4) }).map((_, index) => (
                      <button
                        key={index}
                        className={`h-1.5 rounded-full transition-all ${
                          currentSlide === index ? 'bg-[#9b87f5] w-4' : 'bg-[#E5DEFF] w-2'
                        }`}
                        onClick={() => {
                          setCurrentSlide(index);
                          const startIndex = index * 4;
                          const endIndex = Math.min(startIndex + 4, atsApps.length);
                          
                          if (endIndex > atsApps.length) {
                            const firstPart = atsApps.slice(startIndex);
                            const secondPart = atsApps.slice(0, endIndex - atsApps.length);
                            setVisibleAtsApps([...firstPart, ...secondPart]);
                          } else {
                            setVisibleAtsApps(atsApps.slice(startIndex, endIndex));
                          }
                        }}
                        aria-label={`Go to slide ${index + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div className="absolute -bottom-4 -left-4 bg-[#F8F7FF] border border-[#9b87f5]/30 p-4 rounded-lg shadow-md hidden md:block">
                <p className="font-medium text-[#5E4BD2]">Two-click integration</p>
                <p className="text-sm text-muted-foreground">No coding required</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Practice Interview Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#5E4BD2] to-[#7662DA]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="mb-6">
              <span className="inline-block text-xs bg-white/20 text-white px-3 py-1 rounded-full mb-4 backdrop-blur-sm">
                NEW FEATURE FOR CANDIDATES
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Candidates Can Practice Interviews with AI
            </h2>
            <p className="text-white/90 text-lg mb-8 leading-relaxed">
              Share this free tool with your candidate pool! Candidates can prepare for interviews with our 
              AI-powered practice tool - choose job roles, select skills to focus on, and practice with our 
              realistic AI interviewer - no login required.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10 max-w-2xl mx-auto text-left">
              <div className="flex items-start gap-3">
                <div className="bg-white/20 p-2 rounded-full backdrop-blur-sm">
                  <Brain className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h4 className="font-medium text-white">Better Prepared Candidates</h4>
                  <p className="text-white/80 text-sm">Higher quality conversations in actual interviews</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-white/20 p-2 rounded-full backdrop-blur-sm">
                  <Award className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h4 className="font-medium text-white">Enhances Candidate Experience</h4>
                  <p className="text-white/80 text-sm">Provide value even to candidates you don't hire</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-white/20 p-2 rounded-full backdrop-blur-sm">
                  <GraduationCap className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h4 className="font-medium text-white">Free Employer Branding</h4>
                  <p className="text-white/80 text-sm">Share with your network to boost your employer brand</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-white/20 p-2 rounded-full backdrop-blur-sm">
                  <Zap className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h4 className="font-medium text-white">No Account Required</h4>
                  <p className="text-white/80 text-sm">Easy for candidates to access and use</p>
                </div>
              </div>
            </div>
            
            <a 
              href="https://mock.roundone.ai?utm_source=roundone.ai&utm_medium=homepage&utm_campaign=practice"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button 
                variant="light-outline" 
                size="lg" 
                className="rounded-full border-white text-white bg-white/10 transition-all duration-300 hover:bg-white hover:text-[#5E4BD2] hover:shadow-lg"
              >
                Candidates, practice your interview here
                <ArrowRight className="ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-20 bg-[#F8F7FF]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="uppercase text-sm font-medium tracking-wider text-[#9b87f5] mb-2">MORE FEATURES</p>
            <h2 className="text-3xl font-bold mb-4">Beyond Basic Screening Tools</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Our AI-powered platform delivers lightning-fast screening with unmatched accuracy
            </p>
          </div>
          
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-white to-white/90 rounded-xl p-8 shadow-sm border border-[#E5DEFF] transition-all duration-300 hover:shadow-lg hover:border-[#9b87f5] hover:translate-y-[-5px] group relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-tr from-[#9b87f5]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="bg-[#F8F7FF] p-4 rounded-xl w-16 h-16 mb-6 flex items-center justify-center transition-colors duration-300 group-hover:bg-[#9b87f5] relative z-10">
                  <Headphones className="h-7 w-7 text-[#9b87f5] transition-all duration-300 group-hover:text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-4 relative z-10">Voice & Phone Screening</h3>
                <p className="text-muted-foreground relative z-10">
                  Candidates complete screenings by phone with our natural-sounding AI interviewer, saving you hours of calls.
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-white to-white/90 rounded-xl p-8 shadow-sm border border-[#E5DEFF] transition-all duration-300 hover:shadow-lg hover:border-[#9b87f5] hover:translate-y-[-5px] group relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-tr from-[#9b87f5]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="bg-[#F8F7FF] p-4 rounded-xl w-16 h-16 mb-6 flex items-center justify-center transition-colors duration-300 group-hover:bg-[#9b87f5] relative z-10">
                  <MessagesSquare className="h-7 w-7 text-[#9b87f5] transition-all duration-300 group-hover:text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-4 relative z-10">Custom Questions</h3>
                <p className="text-muted-foreground relative z-10">
                  Create role-specific screening questions to evaluate exactly the skills that matter, cutting down bad-fit interviews.
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-white to-white/90 rounded-xl p-8 shadow-sm border border-[#E5DEFF] transition-all duration-300 hover:shadow-lg hover:border-[#9b87f5] hover:translate-y-[-5px] group relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-tr from-[#9b87f5]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="bg-[#F8F7FF] p-4 rounded-xl w-16 h-16 mb-6 flex items-center justify-center transition-colors duration-300 group-hover:bg-[#9b87f5] relative z-10">
                  <ScanSearch className="h-7 w-7 text-[#9b87f5] transition-all duration-300 group-hover:text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-4 relative z-10">Time-Saving Analytics</h3>
                <p className="text-muted-foreground relative z-10">
                  Get instant candidate rankings, skill scores, and AI-generated summaries that cut your decision time by 80%.
                </p>
              </div>
            </div>
            
            <div className="mt-12 text-center">
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link to="/request-demo">
                  <Button 
                    className="bg-white text-roundone-dark-purple py-3 px-6 rounded-full transition-all duration-300 hover:bg-white/90 hover:shadow-lg hover:scale-105 flex items-center justify-center w-full sm:w-auto"
                  >
                    Book a Demo
                    <Headphones className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/sign-in?tab=register">
                  <Button 
                    className="gradient-button py-3 px-6 rounded-full transition-all duration-300 hover:shadow-lg hover:scale-105 flex items-center justify-center w-full sm:w-auto"
                  >
                    Get started for free
                    <Zap className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Why Teams Love Our Screening Solution</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Join companies cutting costs and slashing time-to-hire by up to 60%
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-[#F8F7FF] border-none shadow-sm rounded-xl overflow-hidden transition-all duration-300 hover:shadow-md hover:transform hover:translate-y-[-5px]">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  {[1, 2, 3, 4, 5].map(star => (
                    <svg key={star} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 mb-4">
                  "RoundOne AI has completely transformed our technical screening process. We've reduced time-to-hire by 40% and improved quality of hires."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#9b87f5] flex items-center justify-center text-white font-bold transition-transform duration-300 hover:scale-110">
                    JD
                  </div>
                  <div>
                    <p className="font-medium">Jamie Diaz</p>
                    <p className="text-sm text-muted-foreground">Tech Recruiter, CloudTech</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-[#F8F7FF] border-none shadow-sm rounded-xl overflow-hidden transition-all duration-300 hover:shadow-md hover:transform hover:translate-y-[-5px]">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  {[1, 2, 3, 4, 5].map(star => (
                    <svg key={star} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 mb-4">
                  "The phone assessment feature is a game-changer. Candidates love the flexibility and we get detailed insights without scheduling countless initial interviews."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#9b87f5] flex items-center justify-center text-white font-bold transition-transform duration-300 hover:scale-110">
                    SP
                  </div>
                  <div>
                    <p className="font-medium">Sarah Parker</p>
                    <p className="text-sm text-muted-foreground">HR Director, Nova Systems</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-[#F8F7FF] border-none shadow-sm rounded-xl overflow-hidden transition-all duration-300 hover:shadow-md hover:transform hover:translate-y-[-5px]">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  {[1, 2, 3, 4, 5].map(star => (
                    <svg key={star} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 mb-4">
                  "The AI analysis gives us insights we couldn't get from traditional screening. It's like having an expert technical recruiter on the team 24/7."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#9b87f5] flex items-center justify-center text-white font-bold transition-transform duration-300 hover:scale-110">
                    RL
                  </div>
                  <div>
                    <p className="font-medium">Ray Liang</p>
                    <p className="text-sm text-muted-foreground">CTO, Bright Solutions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 md:py-20 bg-[#F8F7FF]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="uppercase text-sm font-medium tracking-wider text-[#9b87f5] mb-2">PRICING</p>
            <h2 className="text-3xl font-bold mb-4">Simple, Transparent Pricing</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Choose the plan that works best for your hiring needs
            </p>
            
            {/* Billing toggle */}
            <div className="flex flex-col items-center justify-center gap-4 mt-8">
              <div className="bg-roundone-purple/20 p-1.5 rounded-full flex items-center">
                <button
                  onClick={() => setSelectedInterval('month')}
                  className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
                    selectedInterval === 'month' 
                      ? 'bg-white text-roundone-purple shadow-sm' 
                      : 'text-roundone-purple hover:bg-white/10'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setSelectedInterval('year')}
                  className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                    selectedInterval === 'year' 
                      ? 'bg-white text-roundone-purple shadow-sm' 
                      : 'text-roundone-purple hover:bg-white/10'
                  }`}
                >
                  <span>Annual</span>
                  <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                    Save 25%
                  </span>
                </button>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Basic Plan */}
            <div className="bg-white rounded-2xl p-6 relative overflow-hidden flex flex-col border border-gray-100 shadow-sm">
              <div className="mb-6">
                <h3 className="text-sm font-medium uppercase tracking-wide text-gray-600">
                  BASIC (FREE FOREVER)
                </h3>
                <div className="flex items-baseline gap-1 mt-3">
                  <span className="text-4xl font-bold">£0</span>
                  <span className="text-sm text-gray-500">/month</span>
                </div>
                <p className="text-sm mt-3 text-gray-500">
                  For startups & teams exploring AI-powered hiring.
                </p>
              </div>
              
              <div className="flex-grow space-y-3 mb-6">
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 flex-shrink-0 text-[#9b87f5]" />
                  <span className="text-sm">20 Candidate Assessments</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 flex-shrink-0 text-[#9b87f5]" />
                  <span className="text-sm">Create Unlimited Assessments</span>
                </div>
                <div className="flex items-center gap-2">
                  <X className="h-5 w-5 flex-shrink-0 text-gray-300" />
                  <span className="text-sm text-gray-400">ATS Integration</span>
                </div>
                <div className="flex items-center gap-2">
                  <X className="h-5 w-5 flex-shrink-0 text-gray-300" />
                  <span className="text-sm text-gray-400">AI Detector (Flags AI-generated responses)</span>
                </div>
                <div className="flex items-center gap-2">
                  <X className="h-5 w-5 flex-shrink-0 text-gray-300" />
                  <span className="text-sm text-gray-400">Single Sign-On (SSO)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 flex-shrink-0 text-[#9b87f5]" />
                  <span className="text-sm">Custom Questions (Collect responses to specific questions)</span>
                </div>
                <div className="flex items-center gap-2">
                  <X className="h-5 w-5 flex-shrink-0 text-gray-300" />
                  <span className="text-sm text-gray-400">Custom Domain and Branding</span>
                </div>
                <div className="flex items-center gap-2">
                  <X className="h-5 w-5 flex-shrink-0 text-gray-300" />
                  <span className="text-sm text-gray-400">Priority Phone Support</span>
                </div>
                <div className="flex items-center gap-2">
                  <X className="h-5 w-5 flex-shrink-0 text-gray-300" />
                  <span className="text-sm text-gray-400">Dedicated Account Manager</span>
                </div>
              </div>
              
              <Link to="/sign-in?tab=register">
                <Button 
                  className="w-full rounded-full text-sm font-medium bg-[#B197FC] text-white hover:bg-[#B197FC]/90"
                >
                  Get Started
                </Button>
              </Link>
            </div>
            
            {/* Premium Plan */}
            <div className="bg-white rounded-2xl p-6 relative overflow-hidden flex flex-col border border-gray-100 shadow-sm">
              <div className="mb-6">
                <h3 className="text-sm font-medium uppercase tracking-wide text-gray-600">
                  PREMIUM
                </h3>
                <div className="flex items-baseline gap-1 mt-3">
                  <span className="text-4xl font-bold">£{selectedInterval === 'month' ? '249' : '2490'}</span>
                  <span className="text-sm text-gray-500">/{selectedInterval}</span>
                </div>
                <p className="text-sm mt-3 text-gray-500">
                  For small TA teams focused on hiring top talent quickly
                </p>
              </div>
              
              <div className="flex-grow space-y-3 mb-6">
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 flex-shrink-0 text-[#9b87f5]" />
                  <span className="text-sm">100 Candidate Assessments</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 flex-shrink-0 text-[#9b87f5]" />
                  <span className="text-sm">Create Unlimited Assessments</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 flex-shrink-0 text-[#9b87f5]" />
                  <span className="text-sm">ATS Integration</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 flex-shrink-0 text-[#9b87f5]" />
                  <span className="text-sm">AI Detector (Flags AI-generated responses)</span>
                </div>
                <div className="flex items-center gap-2">
                  <X className="h-5 w-5 flex-shrink-0 text-gray-300" />
                  <span className="text-sm text-gray-400">Single Sign-On (SSO)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 flex-shrink-0 text-[#9b87f5]" />
                  <span className="text-sm">Custom Questions (Collect responses to specific questions)</span>
                </div>
                <div className="flex items-center gap-2">
                  <X className="h-5 w-5 flex-shrink-0 text-gray-300" />
                  <span className="text-sm text-gray-400">Custom Domain and Branding</span>
                </div>
                <div className="flex items-center gap-2">
                  <X className="h-5 w-5 flex-shrink-0 text-gray-300" />
                  <span className="text-sm text-gray-400">Priority Phone Support</span>
                </div>
                <div className="flex items-center gap-2">
                  <X className="h-5 w-5 flex-shrink-0 text-gray-300" />
                  <span className="text-sm text-gray-400">Dedicated Account Manager</span>
                </div>
              </div>
              
              <Link to="/sign-in?tab=register">
                <Button 
                  className="w-full rounded-full text-sm font-medium bg-white text-roundone-purple hover:bg-white/90"
                >
                  Get Started
                </Button>
              </Link>
            </div>
            
            {/* Agency Plan */}
            <div className="bg-roundone-dark-purple rounded-2xl p-6 relative overflow-hidden flex flex-col shadow-md">
              <div className="absolute top-2 right-2">
                <Badge className="bg-[#B197FC] text-white border-none uppercase text-xs font-medium px-3 py-0.5 rounded-full">
                  Most Popular
                </Badge>
              </div>
              
              <div className="mb-6">
                <h3 className="text-sm font-medium uppercase tracking-wide text-white/80">
                  AGENCY
                </h3>
                <div className="flex items-baseline gap-1 mt-3">
                  <span className="text-4xl font-bold text-white">£{selectedInterval === 'month' ? '999' : '9990'}</span>
                  <span className="text-sm text-white/60">/{selectedInterval}</span>
                </div>
                <p className="text-sm mt-3 text-white/60">
                  For agencies and enterprises with high-volume hiring needs.
                </p>
              </div>
              
              <div className="flex-grow space-y-3 mb-6">
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 flex-shrink-0 text-white" />
                  <span className="text-sm text-white">500 Candidate Assessments</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 flex-shrink-0 text-white" />
                  <span className="text-sm text-white">Create Unlimited Assessments</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 flex-shrink-0 text-white" />
                  <span className="text-sm text-white">ATS Integration</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 flex-shrink-0 text-white" />
                  <span className="text-sm text-white">AI Detector (Flags AI-generated responses)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 flex-shrink-0 text-white" />
                  <span className="text-sm text-white">Single Sign-On (SSO)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 flex-shrink-0 text-white" />
                  <span className="text-sm text-white">Custom Questions (Collect responses to specific questions)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 flex-shrink-0 text-white" />
                  <span className="text-sm text-white">Custom Domain and Branding</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 flex-shrink-0 text-white" />
                  <span className="text-sm text-white">Priority Phone Support</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 flex-shrink-0 text-white" />
                  <span className="text-sm text-white">Dedicated Account Manager</span>
                </div>
              </div>
              
              <Link to="/sign-in?tab=register">
                <Button 
                  className="w-full rounded-full text-sm font-medium bg-white text-roundone-purple hover:bg-white/90"
                >
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
          
          {/* Enterprise banner */}
          <div className="mt-16 max-w-6xl mx-auto bg-gradient-to-r from-roundone-purple/10 to-roundone-light-purple/10 rounded-xl p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-2xl font-bold mb-2">Need a custom solution?</h3>
                <p className="text-muted-foreground">
                  Contact our sales team to create a plan tailored to your organization's unique requirements.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/request-demo">
                  <Button 
                    className="bg-white text-roundone-dark-purple py-3 px-6 rounded-full transition-all duration-300 hover:bg-white/90 hover:shadow-lg hover:scale-105 flex items-center justify-center whitespace-nowrap"
                  >
                    Book a Demo
                    <Headphones className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/sign-in?tab=register">
                  <Button 
                    className="gradient-button py-3 px-6 rounded-full transition-all duration-300 hover:shadow-lg hover:scale-105 flex items-center justify-center whitespace-nowrap"
                  >
                    Get started for free
                    <Zap className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Latest from Our Blog</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Insights and updates on recruitment, AI technology, and more
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {blogsLoading ? (
              Array(3).fill(0).map((_, i) => (
                <Card key={i} className="bg-white border border-gray-100 shadow-sm rounded-xl overflow-hidden">
                  <div className="p-6">
                    <div className="h-6 bg-gray-100 rounded w-3/4 mb-4"></div>
                    <div className="h-4 bg-gray-100 rounded w-full mb-2"></div>
                    <div className="h-4 bg-gray-100 rounded w-full mb-2"></div>
                    <div className="h-4 bg-gray-100 rounded w-2/3"></div>
                  </div>
                </Card>
              ))
            ) : blogPosts.length > 0 ? (
              blogPosts.slice(0, 3).map((post) => (
                <Card key={post.id} className="bg-white border border-gray-100 shadow-sm rounded-xl overflow-hidden transition-all duration-300 hover:shadow-md hover:transform hover:translate-y-[-5px]">
                  {post.featured_image && (
                    <Link to={`/blog/${post.slug}`} className="block">
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={post.featured_image}
                          alt={post.title}
                          className="w-full h-full object-cover transition-transform hover:scale-105"
                        />
                      </div>
                    </Link>
                  )}
                  <CardContent className="p-6">
                    <div className="text-xs text-muted-foreground mb-2">
                      {formatDate(post.published_at || post.created_at)}
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{post.title}</h3>
                    <p className="text-muted-foreground line-clamp-3 mb-4">
                      {post.excerpt || post.content.substring(0, 120) + '...'}
                    </p>
                    <Link to={`/blog/${post.slug}`} className="text-sm text-roundone-purple hover:underline">
                      Read more
                    </Link>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="col-span-3 text-center py-8">
                <p>No blog posts found.</p>
              </div>
            )}
          </div>
          
          <div className="text-center mt-8">
            <Link to="/blog">
              <Button variant="outline" className="rounded-full">
                View all articles
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <FrequentlyAskedQuestions />

      {/* Slash Your Screening Time Section */}
      <ProvenResults />

      {/* Use the shared Footer component */}
      <Footer />
    </div>
  );
};

export default HomePage; 